import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import CampaignContextProvider from "../../../context/campaign/provider";
import CampaignContext from "../../../context/campaign/context";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import { Button, Col, Divider, Row, Switch } from "antd";
import { FormProvider, useForm } from "react-hook-form";
import { getTranslations } from "../../../utils/helpers/translations";
import { IUpdateCampaign } from "../../../models/campaign/request";
import { execute } from "../../../utils/helpers/execute";
import styles from "../../trademark/trademark-form/style.module.scss";
import Spin from "../../general/antd/spin";
import { languages } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import FormItem from "../../general/form-item";
import Controller from "../../form-components/controller";
import ImageUploader from "../../form-components/image-uploader";
import { MediumFor } from "../../../models/medium/enum";
import OldImage from "../../form-components/old-image";
import EndPoints from "../../../services/end-points";
import { ICity } from "../../../models/city/response";
import { ICategoryCampaign } from "../../../models/category_campaign/response";
import { ICharity } from "../../../models/charity/response";
import {
    CampaignStatus,
    DonationGoal,
    getCampaignStatusName,
    getPeriodicTypeName,
    PeriodicType
} from "../../../models/campaign/enums";

const Form = () => {
    const { t } = useTranslation();
    const { details, actions, loading } = useContext(CampaignContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const { direction } = useContext(AppContext);
    const [cities, setCities] = useState<ICity[]>([]);
    const [citiesLoading, setCitiesLoading] = useState(true);
    const [categories, setCategories] = useState<ICategoryCampaign[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [charities, setCharities] = useState<ICharity[]>([]);
    const [charitiesLoading, setCharitiesLoading] = useState(true);

    const methods = useForm<any>({
        defaultValues: {
            name: getTranslations(details?.translations?.name),
            description: getTranslations(details?.translations?.description),
            content: getTranslations(details?.translations?.content),
            donationUnit: details?.donationUnit,
            donationGoal: details?.donationGoal,
            periodic:details?.periodic ?? false,
            responsible:details?.responsible,
            mobile:details?.mobile,
            telegram:details?.telegram,
            whatsApp:details?.whatsApp,
            facebook:details?.facebook,
            instagram:details?.instagram,
            caseNumber:details?.caseNumber,
            startDate:details?.startDate,
            periodicType: details?.periodicType ?? [],
            endDate:details?.endDate,
            targetAmount:details?.targetAmount??0,
            paidAmount:details?.paidAmount??0,
            imageId:details?.image?.id,
            coverId:details?.cover?.id,
            cityId:details?.city?.id,
            categoryId:details?.category?.id,
            charityId:details?.charity?.id,
            status:details?.status
        } as IUpdateCampaign,
    });

    const {
        handleSubmit,
        reset,
        control,
        setValue,
        watch,
        formState: { errors },
    } = methods;

    const isPeriodic = watch('periodic');

    useEffect(() => {
        const getCategories = async () => {
            try {
                const { data } = await EndPoints.categoryCampaign.getAllCategoriesCampaigns({
                    light: true,
                    perPage: -1,
                });
                setCategories(data.data);
            } catch (error) {
            } finally {
                setCategoriesLoading(false);
            }
        };
        getCategories();
    }, [direction]);

    useEffect(() => {
        const getCharities = async () => {
            try {
                const { data } = await EndPoints.charity.getAllCharities({
                    light: true,
                    perPage: -1,
                });
                setCharities(data.data);
            } catch (error) {
            } finally {
                setCharitiesLoading(false);
            }
        };
        getCharities();
    }, [direction]);

    useEffect(() => {
        const getCities = async () => {
            try {
                const { data } = await EndPoints.city.getAllCities({
                    light: true,
                    perPage: -1,
                });
                setCities(data.data);
                setCitiesLoading(false);
            } catch (error) {
            } finally {
                setCitiesLoading(false);
            }
        };
        getCities();
    }, [direction]);

    useEffect(() => {
        reset({
            name: getTranslations(details?.translations?.name),
            description: getTranslations(details?.translations?.description),
            content: getTranslations(details?.translations?.content),
            donationUnit: details?.donationUnit,
            donationGoal: details?.donationGoal,
            periodic:details?.periodic ?? false,
            responsible:details?.responsible,
            mobile:details?.mobile,
            telegram:details?.telegram,
            whatsApp:details?.whatsApp,
            facebook:details?.facebook,
            instagram:details?.instagram,
            caseNumber:details?.caseNumber,
            periodicType: details?.periodicType ?? [],
            startDate:details?.startDate,
            endDate:details?.endDate,
            targetAmount:details?.targetAmount??0,
            paidAmount:details?.paidAmount??0,
            imageId:details?.image?.id,
            coverId:details?.cover?.id,
            cityId:details?.city?.id,
            categoryId:details?.category?.id,
            charityId:details?.charity?.id,
            status:details?.status
        } as IUpdateCampaign);
    }, [details]);

    useEffect(() => {
        id && actions.getDetails(Number(id));
    }, [id]);

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const onSubmit = async (data: any) => {
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                details
                    ? await actions.updateCampaign(details?.id, data)
                    : await actions.createCampaign(data);
                navigate(-1);
            },
            fallback: (error) => {
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <span className={styles.title}>
                        {details ? t("update_campaign") : t("add_campaign")}
                    </span>
                    <Divider className={styles.divider} />
                    {loading.includes("details") ? (
                        <div style={{ height: "60vh" }}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Row gutter={[16, 8]}>
                                {/* Name Field */}
                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`name[${lang?.code}]`}
                                            label={`${t("name")} (${lang?.name})`}
                                            input={{ type: "text" }}
                                            rules={{ required: true }}
                                        />
                                    </Col>
                                ))}

                                {/* Description Field */}
                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`description[${lang?.code}]`}
                                            label={`${t("description")} (${lang?.name})`}
                                            input={{ type: "text-area" }}
                                            rules={{ required: false }}
                                        />
                                    </Col>
                                ))}

                                {/* Content Field */}
                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`content[${lang?.code}]`}
                                            label={`${t("content")} (${lang?.name})`}
                                            input={{ type: "text-area" }}
                                            rules={{ required: false }}
                                        />
                                    </Col>
                                ))}

                                {/* Campaign Details */}
                                <Col span={12}>
                                    <FieldBuilder
                                        name="donationGoal"
                                        label={t("donation_goal")}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            options: Object.entries(DonationGoal).filter(([key]) => isNaN(Number(key))).map(([key, value]) => ({
                                                key,
                                                label: t(key.toLowerCase()),
                                                value,
                                            })),
                                        }}
                                    />
                                </Col>

                                <Col span={12}>
                                    <FormItem label={t("periodic")}>
                                        <Controller
                                            name="periodic"
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value}
                                                    onChange={(checked) => {
                                                        field.onChange(checked);
                                                        if (!checked) {
                                                            setValue("periodicType", []);
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </FormItem>
                                </Col>

                                {isPeriodic && (
                                    <Col span={12}>
                                        <FieldBuilder
                                            name="periodicType"
                                            label={t("periodic_type")}
                                            width="large"
                                            rules={{ required: isPeriodic }}
                                            input={{
                                                type: "multiSelect",
                                                options: Object.entries(PeriodicType).filter(([key]) => isNaN(Number(key))).map(([key, value]) => ({
                                                    key,
                                                    label: t(getPeriodicTypeName(Number(value))),
                                                    value,
                                                })),
                                            }}
                                        />
                                    </Col>
                                )}

                                <Col span={12}>
                                    <FieldBuilder
                                        name="responsible"
                                        label={t("responsible")}
                                        input={{ type: "text" }}
                                    />
                                </Col>

                                <Col span={12}>
                                    <FieldBuilder
                                        name="mobile"
                                        label={t("mobile")}
                                        input={{ type: "text" }}
                                    />
                                </Col>

                                {/* Social Media Fields */}
                                <Col span={12}>
                                    <FieldBuilder
                                        name="telegram"
                                        label={t("telegram")}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="whatsApp"
                                        label={t("whatsApp")}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="facebook"
                                        label={t("facebook")}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="instagram"
                                        label={t("instagram")}
                                        input={{ type: "text" }}
                                    />
                                </Col>

                                {/* Campaign Details */}
                                <Col span={12}>
                                    <FieldBuilder
                                        name="caseNumber"
                                        label={t("case_number")}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="donationUnit"
                                        label={t("donation_unit")}
                                        input={{ type: "number" }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="startDate"
                                        label={t("start_date")}
                                        input={{ type: "date-picker", format: "YYYY-MM-DD" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="endDate"
                                        label={t("end_date")}
                                        input={{ type: "date-picker", format: "YYYY-MM-DD" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="targetAmount"
                                        label={t("target_amount")}
                                        input={{ type: "number" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="status"
                                        label={t("status")}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            options: Object.entries(CampaignStatus).filter(([key]) => isNaN(Number(key))).map(([key, value]) => ({
                                                key,
                                                label: t(getCampaignStatusName(Number(value))),
                                                value,
                                            })),
                                        }}
                                    />
                                </Col>

                                {/* Dropdowns for IDs */}
                                <Col span={12}>
                                    <FieldBuilder
                                        name="cityId"
                                        label={`${t("city")}`}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            loading: citiesLoading,
                                            allowSearch: true,
                                            options: cities?.map((city, index) => {
                                                return {
                                                    key: index,
                                                    label: city.name,
                                                    value: city.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="categoryId"
                                        label={`${t("category")}`}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            loading: categoriesLoading,
                                            allowSearch: true,
                                            options: categories?.map((category, index) => {
                                                return {
                                                    key: index,
                                                    label: category.name,
                                                    value: category.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="charityId"
                                        label={`${t("charity")}`}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            loading: charitiesLoading,
                                            allowSearch: true,
                                            options: charities?.map((charity, index) => {
                                                return {
                                                    key: index,
                                                    label: charity.name,
                                                    value: charity.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>

                                {/* Image Uploaders */}
                                <Col span={24}>
                                    <FormItem label={t("image")} required>
                                        <Controller
                                            name="imageId"
                                            rules={{ required: true }}
                                            render={({ field: { ref, ...field } }) => (
                                                <ImageUploader
                                                    {...field}
                                                    type="DRAGGER"
                                                    onChange={(response) => field.onChange(response?.id)}
                                                    imageFor={MediumFor.UserProfile}
                                                />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                                {details && (
                                    <>
                                        <OldImage src={details?.image?.url} />
                                    </>
                                )}
                                <Col span={24}>
                                    <FormItem label={t("cover")}>
                                        <Controller
                                            name="coverId"
                                            render={({ field: { ref, ...field } }) => (
                                                <ImageUploader
                                                    {...field}
                                                    type="DRAGGER"
                                                    onChange={(response) => field.onChange(response?.id)}
                                                    imageFor={MediumFor.UserProfile}
                                                />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                                {details && (
                                    <>
                                        <OldImage src={details?.cover?.url} />
                                    </>
                                )}
                            </Row>
                        </div>
                    )}
                </div>
                <div>
                    <div className={styles.formFooter}>
                        <Button onClick={() => navigate(-1)}>
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    )
}

const CampaignForm = () => {
    return (
        <CampaignContextProvider>
            <Form />
        </CampaignContextProvider>
    );
};
export default CampaignForm;